import React, { useCallback, useState } from 'react';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  BackgroundVariant,
  NodeTypes,
  EdgeTypes,
  MarkerType,
  Position,
  Handle,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './styles.css';
import CustomEdge from './custom-edge';
import { edges } from './edges';
import { 
  nodes, 
  nodeTypeConfig, 
  baseNodeStyles, 
  handleStyles, 
  labelStyles 
} from './nodes';
import { useParams, useNavigate } from 'react-router-dom';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// Generic nav provider for this route: returns sub-links discovered from local displays
export function getNavLinks(basePath: string = '/conversational-framework') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ctx: any = (require as any).context('./displays', false, /\.json$/);
  const ids = ctx.keys().map((k: string) => k.replace('./', '').replace('.json', '')) as string[];
  return ids.map((id) => ({ to: `${basePath}/${id}`, label: id }));
}

// Custom node component for framework boxes
const FrameworkNode = ({ data }: { data: any }) => {
  const { type, label, isActive, isGlowing } = data;
  
  // Get node styles based on type from configuration
  const getNodeStyles = (nodeType: string) => {
    const typeConfig = nodeTypeConfig[nodeType as keyof typeof nodeTypeConfig] || {
      background: '#f8f9fa',
      borderColor: '#6c757d',
      label: 'Default'
    };

    return {
      ...baseNodeStyles,
      ...typeConfig,
    };
  };

  const handleSpacing = -1.5;

  const nodeStyle = {
    ...getNodeStyles(type),
    filter: isActive === false ? 'grayscale(100%)' : (isGlowing ? 'drop-shadow(0 0 4px #FFD700) drop-shadow(0 0 8px #FFD700)' : 'none'),
    opacity: isActive === false ? 0.5 : 1,
    transition: 'all 0.3s ease',
    borderColor: isGlowing ? '#FFD700' : getNodeStyles(type).borderColor,
    background: isGlowing ? '#FFF9E6' : getNodeStyles(type).background,
  };

  return (
    <div style={nodeStyle}>
      <Handle
        type="source"
        position={Position.Top}
        id="source-top"
        style={{ ...handleStyles, top: handleSpacing }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="source-left"
        style={{ ...handleStyles, left: handleSpacing }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="source-bottom"
        style={{ ...handleStyles, bottom: handleSpacing }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="source-right"
        style={{ ...handleStyles, right: handleSpacing }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="target-top"
        style={{ ...handleStyles, top: handleSpacing }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="target-left"
        style={{ ...handleStyles, left: handleSpacing }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="target-bottom"
        style={{ ...handleStyles, bottom: handleSpacing }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="target-right"
        style={{ ...handleStyles, right: handleSpacing }}
      />
      <div>
        <h4 style={{ ...labelStyles, fontWeight: type === 'learner' ? 800 : labelStyles.fontWeight }}>{label}</h4>
      </div>
    </div>
  );
};

// Define nodeTypes outside component to prevent recreation
const nodeTypes: NodeTypes = {
  frameworkNode: FrameworkNode,
};

// Define edgeTypes to include our custom CustomEdge
const edgeTypes: EdgeTypes = {
  customEdge: CustomEdge,
};

const ConversationalFramework: React.FC = () => {
  const [selectedButton, setSelectedButton] = useState<string | null>(null);
  const params = useParams();
  const navigate = useNavigate();

  // Load all display JSON files under ./displays at build time
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const displaysContext: any = (require as any).context('./displays', false, /\.json$/);
  const displayIds = displaysContext.keys().map((k: string) => k.replace('./', '').replace('.json', ''));
  const currentDisplayId = (params as any).displayId && displayIds.includes((params as any).displayId as string)
    ? ((params as any).displayId as string)
    : displayIds[0];

  // Redirect to first available display if none specified
  React.useEffect(() => {
    if (!(params as any).displayId && currentDisplayId) {
      navigate(`/conversational-framework/${currentDisplayId}`, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDisplayId]);

  // Resolve selected display data
  const rawModule = displaysContext(`./${currentDisplayId}.json`);
  // Some bundlers attach JSON under .default; handle both
  const displayData = rawModule?.default ?? rawModule;
  const displayName: string = displayData?.name ?? 'Display';
  const frameworkList = (displayData?.items ?? []) as any[];
  const frameworkById = (frameworkList as any[]).reduce((acc, phase: any) => {
    acc[phase.id] = phase;
    return acc;
  }, {} as Record<string, any>);
  const [currentAnimationIndex, setCurrentAnimationIndex] = useState(0);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);
  const [expandedId, setExpandedId] = useState<string | false>(false);

  const [nodesState, setNodes, onNodesChange] = useNodesState(nodes);
  const [edgesState, setEdges, onEdgesChange] = useEdgesState(edges);

  // Store original edge colors for restoration
  const originalEdgesRef = React.useRef<Map<string, { stroke: string; markerColor: any }>>(new Map());

  // Initialize original edge colors once
  React.useEffect(() => {
    if (originalEdgesRef.current.size === 0) {
      edges.forEach(edge => {
        originalEdgesRef.current.set(edge.id, {
          stroke: edge.style?.stroke || '#000000',
          markerColor: edge.markerEnd && typeof edge.markerEnd === 'object' ? edge.markerEnd.color : undefined,
        });
      });
    }
  }, []);

  // Smooth progress animation for current edge
  React.useEffect(() => {
    if (!selectedButton) {
      setAnimationProgress(0);
      return;
    }

    const phase = frameworkById[selectedButton as keyof typeof frameworkById];
    if (!phase.animatedEdgeIds || phase.animatedEdgeIds.length === 0) {
      return;
    }

    // Reset progress when switching edges
    setAnimationProgress(0);

    // Smoothly increment progress from 0 to 1
    const duration = 1500; // 1.5 seconds to fill
    const fps = 60;
    const increment = 1 / (duration / (1000 / fps));
    
    const interval = setInterval(() => {
      setAnimationProgress((prev) => {
        const next = prev + increment;
        if (next >= 1) {
          return 1;
        }
        return next;
      });
    }, 1000 / fps);

    return () => clearInterval(interval);
  }, [selectedButton, currentAnimationIndex, animationKey]);

  // Sequential animation loop - move to next edge after current fills
  React.useEffect(() => {
    if (!selectedButton) {
      setCurrentAnimationIndex(0);
      return;
    }

    const phase = frameworkById[selectedButton as keyof typeof frameworkById];
    if (!phase.animatedEdgeIds || phase.animatedEdgeIds.length === 0) {
      return;
    }

    if (animationProgress >= 1) {
      // For single-edge animations, increment the animation key to force restart
      if (phase.animatedEdgeIds.length === 1) {
        setAnimationKey(prev => prev + 1);
      } else {
        setCurrentAnimationIndex((prevIndex) => 
          (prevIndex + 1) % phase.animatedEdgeIds.length
        );
      }
    }
  }, [selectedButton, animationProgress]);

  // Update nodes and edges based on selected phase and animation index
  React.useEffect(() => {
    if (selectedButton) {
      const phase = frameworkById[selectedButton as keyof typeof frameworkById];
      
      // Find the currently animating edge to get its source node
      const currentEdgeId = phase.animatedEdgeIds[currentAnimationIndex];
      const currentEdge = edges.find(e => e.id === currentEdgeId);
      const currentSourceNodeId = currentEdge?.source;
      
      // Update nodes with isActive flag and gold glow for source node
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          data: {
            ...node.data,
            isActive: phase.activeNodeIds.includes(node.id),
            isGlowing: node.id === currentSourceNodeId && animationProgress > 0,
          },
        }))
      );

      // Update edges with grayscale and sequential animation
      setEdges((eds) =>
        eds.map((edge) => {
          const isActive = phase.activeEdgeIds.includes(edge.id);
          const animationSequenceIndex = phase.animatedEdgeIds.indexOf(edge.id);
          const isInAnimationSequence = animationSequenceIndex >= 0;
          const isCurrentlyAnimating = animationSequenceIndex === currentAnimationIndex;
          
          // Get original stroke color
          const originalColors = originalEdgesRef.current.get(edge.id);
          const baseStroke = originalColors?.stroke || '#000000';
          
          return {
            ...edge,
            style: {
              ...edge.style,
              stroke: isActive ? baseStroke : '#999999',
              opacity: isActive ? 1 : 0.4,
              transition: 'all 0.3s ease',
              // All active edges get dashed lines
              strokeDasharray: isActive ? '5,5' : undefined,
              animation: isActive ? 'dashdraw 0.3s linear infinite' : undefined,
            },
            markerEnd: isActive 
              ? (edge.markerEnd && typeof edge.markerEnd === 'object' 
                ? { 
                    ...edge.markerEnd, 
                    color: originalColors?.markerColor || edge.markerEnd.color,
                  } 
                : edge.markerEnd)
              : (edge.markerEnd && typeof edge.markerEnd === 'object' 
                ? { ...edge.markerEnd, color: '#999999' } 
                : edge.markerEnd),
            data: {
              ...edge.data,
              // All active edges get dashed animation
              animated: isActive,
              // Only currently animating edge gets the gold fill
              showGoldOverlay: isCurrentlyAnimating,
              animationProgress: isCurrentlyAnimating ? animationProgress : 0,
            },
          };
        })
      );
    } else {
      // Reset all when no button selected
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          data: {
            ...node.data,
            isActive: true,
            isGlowing: false,
          },
        }))
      );

      setEdges((eds) =>
        eds.map((edge) => {
          const originalColors = originalEdgesRef.current.get(edge.id);
          return {
            ...edge,
            style: {
              ...edge.style,
              stroke: originalColors?.stroke || edge.style?.stroke || '#000000',
              opacity: 1,
            },
            markerEnd: edge.markerEnd && typeof edge.markerEnd === 'object' 
              ? { ...edge.markerEnd, color: originalColors?.markerColor || edge.markerEnd.color }
              : edge.markerEnd,
            data: {
              ...edge.data,
              animated: edge.data?.animated || false,
            },
          };
        })
      );
    }
  }, [selectedButton, currentAnimationIndex, animationProgress, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  return (
    <Box sx={{ height: '100vh', width: '100vw', position: 'fixed', top: 0, left: 0, display: 'flex', flexDirection: 'row' }}>
      <Box sx={{ width: 180, height: '100%', overflowY: 'auto', bgcolor: 'rgba(255,255,255,0.95)', borderRight: '1px solid #e1e5e9', py: 1, 
        scrollbarWidth: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}>
        <Typography fontWeight={700} sx={{ px: 2, pt: 1, pb: 0.5 }}>
          {displayName}
        </Typography>
        <Box sx={{ width: '100%', height: 8, background: 'linear-gradient(90deg, #007bff 0%, #28a745 100%)', my: 2 }} />
        {frameworkList.map((phase) => (
          <Accordion
            key={phase.id}
            expanded={expandedId === phase.id}
            onChange={(event: React.SyntheticEvent, isExpanded: boolean) => {
              setExpandedId(isExpanded ? phase.id : false);
              setSelectedButton(isExpanded ? phase.id : null);
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0 } }}>
              <Typography variant="body2" fontWeight={700}>
                {phase.label}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0.5, pb: 1, px: 1 }}>
              <Typography variant="body2" align="left">
                {frameworkById[phase.id].description || ''}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
      <Box sx={{ position: 'relative', flex: 1, height: '100%' }}>
        <ReactFlow
          nodes={nodesState}
          edges={edgesState}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          attributionPosition="bottom-left"
        >
          {/* <Controls /> */}
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        </ReactFlow>
      </Box>
    </Box>
  );
};

export default ConversationalFramework;
